/**
 * VulnerabilityModal Component
 *
 * Unified wizard modal for adding AND editing vulnerability nodes in attack paths.
 * Steps (add mode):
 * 1. VulnerabilitySearchStep - Search/select vulnerability or add custom
 * 2. AssetSelectionStep - Select affected asset
 *
 * Steps (edit mode):
 * 1. VulnerabilitySearchStep - Edit vulnerability details only (affected asset not editable)
 *
 * Features:
 * - Supports both 'add' and 'edit' modes via mode prop
 * - Edit mode skips affected asset step (edges managed separately)
 * - useOptimistic for instant node creation/update feedback
 * - Suspense boundaries for declarative loading
 * - ARIA live regions for accessibility
 * - Input sanitization for security
 */

import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useOptimistic,
} from 'react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import { Modal } from '@/components/Modal';
import type { WizardConfig, WizardStep } from '@/components/wizards';
import {
  useWizard,
  useWizardFooterConfig,
  WizardModalFooter,
  WizardProgressBar,
  WizardStepContainer,
} from '@/components/wizards';
import { useAttackState } from '@/sections/attacks/attackPaths/context';
import { useNodeUpdate } from '@/sections/attacks/attackPaths/hooks';
import type {
  CustomAssetNode,
  CustomVulnerabilityNode,
} from '@/sections/attacks/attackPaths/types';

import { StepLoadingSkeleton } from './components';
import { AssetSelectionStep, VulnerabilitySearchStep } from './steps';
import type { VulnerabilityWizardFormData } from './types';
import {
  createVulnerabilityFormData,
  validateAffectedAssetStep,
  validateVulnerabilityStep,
  vulnerabilityFormDataFromNode,
} from './types';

/**
 * Modal mode type
 */
type NodeModalMode = 'add' | 'edit';

/**
 * Props for VulnerabilityModal component
 */
interface VulnerabilityModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Modal mode: 'add' for new nodes, 'edit' for existing nodes */
  mode: NodeModalMode;
  /** For edit mode: existing node data to pre-populate */
  initialNode?: CustomVulnerabilityNode;
  /** Optional callback when node is successfully created/updated */
  onComplete?: () => void;
}

/**
 * Wizard step definitions for add mode (vulnerability + affected asset)
 */
const addModeSteps: WizardStep<VulnerabilityWizardFormData>[] = [
  {
    id: 'vulnerability-selection',
    order: 1,
    title: 'Select Vulnerability',
    description: 'Choose or create a vulnerability to add to the attack path',
    validate: validateVulnerabilityStep,
  },
  {
    id: 'affected-asset-selection',
    order: 2,
    title: 'Select Affected Asset',
    description: 'Choose which asset is affected by this vulnerability',
    validate: validateAffectedAssetStep,
    isFinal: true,
    // Skip this step when Chariot vulnerability is selected (asset comes from metadata)
    shouldSkip: data => !data.isCustom && Boolean(data.affectedAssetId),
  },
];

/**
 * Wizard step definitions for edit mode (vulnerability only)
 */
const editModeSteps: WizardStep<VulnerabilityWizardFormData>[] = [
  {
    id: 'vulnerability-selection',
    order: 1,
    title: 'Edit Vulnerability',
    description: 'Update the vulnerability details',
    validate: validateVulnerabilityStep,
    isFinal: true,
  },
];

/**
 * Creates nodes from wizard form data.
 * Returns both the vulnerability node and optionally an asset node if custom.
 */
function createNodesFromFormData(formData: VulnerabilityWizardFormData): {
  vulnerabilityNode: CustomVulnerabilityNode;
  assetNode?: CustomAssetNode;
} {
  const vulnerabilityNode: CustomVulnerabilityNode = {
    id: uuidv4(),
    type: 'vulnerability',
    position: { x: 0, y: 0 }, // Position will be set by layout
    data: {
      key: formData.vulnerabilityKey,
      name: formData.vulnerabilityName || formData.vulnerabilityKey,
      priority: formData.priority,
      type: formData.isCustom ? 'nonChariot' : undefined,
    },
  };

  // Create asset node if affected asset is custom (doesn't exist in Chariot)
  let assetNode: CustomAssetNode | undefined;
  if (formData.isAffectedAssetCustom) {
    assetNode = {
      id: uuidv4(),
      type: 'asset',
      position: { x: 0, y: 0 },
      data: {
        id: formData.affectedAssetId,
        type: 'nonChariot',
      },
    };
  }

  return { vulnerabilityNode, assetNode };
}

/**
 * Mode-specific labels for UI elements
 */
const MODE_LABELS = {
  add: {
    title: 'Add Vulnerability Node',
    submit: 'Add',
    submitting: 'Adding...',
    successMessage: 'Vulnerability node added to attack path',
    errorMessage: 'Failed to add vulnerability node',
  },
  edit: {
    title: 'Edit Vulnerability Node',
    submit: 'Save',
    submitting: 'Saving...',
    successMessage: 'Vulnerability node updated',
    errorMessage: 'Failed to update vulnerability node',
  },
} as const;

/**
 * VulnerabilityModal - Unified modal wizard for adding/editing vulnerability nodes.
 *
 * Add mode implements a 2-step flow:
 * 1. Vulnerability selection (search existing or add custom)
 * 2. Affected asset selection (reuses AssetSelectionStep)
 *
 * Edit mode implements a 1-step flow:
 * 1. Vulnerability selection only (affected assets are managed via graph edges)
 *
 * Uses React 19 patterns:
 * - useOptimistic for instant node addition/update with automatic rollback
 * - Suspense for declarative loading states
 *
 * @example
 * ```tsx
 * // Add mode
 * <VulnerabilityModal
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   mode="add"
 *   onComplete={() => toast.success('Vulnerability added')}
 * />
 *
 * // Edit mode
 * <VulnerabilityModal
 *   open={editModalOpen}
 *   onClose={() => setEditModalOpen(false)}
 *   mode="edit"
 *   initialNode={existingNode}
 *   onComplete={() => toast.success('Vulnerability updated')}
 * />
 * ```
 */
export function VulnerabilityModal({
  open,
  onClose,
  mode,
  initialNode,
  onComplete,
}: VulnerabilityModalProps) {
  const { nodes, addNode } = useAttackState();
  const { updateVulnerability } = useNodeUpdate(initialNode?.id ?? '');

  const labels = MODE_LABELS[mode];

  // Use different steps based on mode
  const steps = mode === 'edit' ? editModeSteps : addModeSteps;

  // Compute initial data based on mode
  const initialData = useMemo(() => {
    if (mode === 'edit' && initialNode) {
      return vulnerabilityFormDataFromNode(initialNode);
    }
    return createVulnerabilityFormData();
  }, [mode, initialNode]);

  // Optimistic state for instant UI feedback
  const [, addOptimisticNode] = useOptimistic(
    nodes,
    (state, newNodes: (CustomVulnerabilityNode | CustomAssetNode)[]) => [
      ...state,
      ...newNodes,
    ]
  );

  /**
   * Handle wizard submission - create or update the nodes
   */
  const handleSubmit = useCallback(
    async (formData: VulnerabilityWizardFormData) => {
      try {
        if (mode === 'edit' && initialNode) {
          // Edit mode: update existing node
          await updateVulnerability(
            formData.vulnerabilityKey,
            formData.isCustom,
            formData.vulnerabilityName,
            formData.priority
          );
          toast.success(labels.successMessage);
        } else {
          // Add mode: create new nodes
          const { vulnerabilityNode, assetNode } =
            createNodesFromFormData(formData);
          const nodesToAdd = assetNode
            ? [vulnerabilityNode, assetNode]
            : [vulnerabilityNode];

          addOptimisticNode(nodesToAdd);
          nodesToAdd.forEach(node => addNode(node));
          toast.success(labels.successMessage);
        }
        onComplete?.();
        onClose();
      } catch {
        toast.error(labels.errorMessage);
      }
    },
    [
      mode,
      initialNode,
      updateVulnerability,
      addOptimisticNode,
      addNode,
      labels,
      onClose,
      onComplete,
    ]
  );

  // Configure wizard
  const wizardConfig: WizardConfig<VulnerabilityWizardFormData> = useMemo(
    () => ({
      steps,
      initialData,
      onSubmit: handleSubmit,
    }),
    [steps, initialData, handleSubmit]
  );

  const wizard = useWizard(wizardConfig);
  const { reset } = wizard;

  // Reset wizard to step 1 when modal opens
  // This ensures clean state after any close scenario (submit, cancel, escape, click outside)
  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  // Configure footer buttons
  const footerConfig = useWizardFooterConfig({
    canGoNext: wizard.navigation.canGoNext,
    canGoBack: wizard.navigation.canGoBack,
    isPending: wizard.isSubmitting,
    isLastStep: wizard.navigation.isLastStep,
    onNext: wizard.navigation.handleNext,
    onBack: wizard.navigation.handleBack,
    onCancel: () => {
      wizard.reset();
      onClose();
    },
    onSubmit: wizard.submit,
    labels: {
      next: 'Next',
      back: 'Back',
      cancel: 'Cancel',
      submit: labels.submit,
      submitting: labels.submitting,
    },
  });

  /**
   * Handle modal close with reset
   */
  const handleClose = () => {
    wizard.reset();
    onClose();
  };

  /**
   * Render the current step content
   */
  const renderStepContent = () => {
    switch (wizard.currentStepId) {
      case 'vulnerability-selection':
        return (
          <VulnerabilitySearchStep
            formData={wizard.formData}
            setFormData={wizard.setFormData}
            // Pass preselectedKey in edit mode to ensure the selected vulnerability is in the list for auto-scroll
            preselectedKey={
              mode === 'edit' && !initialData.isCustom
                ? initialData.vulnerabilityKey
                : undefined
            }
          />
        );
      case 'affected-asset-selection':
        return (
          <AssetSelectionStep
            title="Select Affected Asset"
            description="Which asset is affected by this vulnerability? Search for an existing Chariot asset or add a custom one."
            formData={{
              assetId: wizard.formData.affectedAssetId,
              isCustom: wizard.formData.isAffectedAssetCustom,
            }}
            onAssetChange={(assetId, isCustom, _displayName) =>
              wizard.setFormData(prev => ({
                ...prev,
                affectedAssetId: assetId,
                isAffectedAssetCustom: isCustom,
              }))
            }
            vulnerabilityKey={wizard.formData.vulnerabilityKey}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={labels.title}
      hideFooter
      size="4xl"
      data-testid={`${mode}-vulnerability-wizard-modal`}
    >
      {/* ARIA live region for step changes */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        Step {wizard.progress.currentDisplayStep} of{' '}
        {wizard.progress.totalDisplaySteps}: {wizard.progress.currentStepTitle}
      </div>

      {/* Progress bar */}
      <WizardProgressBar
        currentStep={wizard.progress.currentDisplayStep}
        totalSteps={wizard.progress.totalDisplaySteps}
        stepTitle={wizard.progress.currentStepTitle}
        percentage={wizard.progress.percentage}
      />

      {/* Step content with Suspense boundary */}
      <WizardStepContainer>
        <Suspense fallback={<StepLoadingSkeleton />}>
          {renderStepContent()}
        </Suspense>
      </WizardStepContainer>

      {/* Error display */}
      {wizard.submitError && (
        <div
          className="rounded-md bg-red-500/10 p-3 text-sm text-red-500"
          role="alert"
        >
          {wizard.submitError}
        </div>
      )}

      {/* Footer with navigation buttons */}
      <WizardModalFooter config={footerConfig} className="mt-0" />
    </Modal>
  );
}
